import { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CameraModal({ user, platforms, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [generatedCaption, setGeneratedCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cameraActive, setCameraActive] = useState(false);

  // Use current_industry for admin switcher, fallback to industry
  const effectiveIndustry = user.current_industry || user.industry;

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraActive(true);
      setError('');
    } catch (err) {
      setError('Unable to access camera. Please check permissions.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/png');
    setCapturedImage(imageData);
    
    // Stop camera
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setCameraActive(false);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setGeneratedCaption('');
    startCamera();
  };

  const generateCaption = async () => {
    if (!capturedImage) {
      setError('Please capture a photo first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/generate/content`, {
        user_id: user.id,
        industry: effectiveIndustry,
        platform: 'General',
        prompt: 'Generate an engaging caption for this captured photo'
      });
      setGeneratedCaption(response.data.content);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate caption');
    } finally {
      setLoading(false);
    }
  };

  const savePost = async () => {
    if (!generatedCaption) {
      setError('Please generate a caption first');
      return;
    }

    setLoading(true);
    try {
      const postResponse = await axios.post(`${API}/posts`, {
        user_id: user.id,
        platform: 'General'
      });

      await axios.put(`${API}/posts/${postResponse.data.id}`, {
        content: generatedCaption,
        image_base64: capturedImage.split(',')[1]
      });

      alert('Post saved successfully!');
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-orange-500/30 rounded-2xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold gradient-text">Take Photo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-3xl">×</button>
        </div>

        {/* Camera View */}
        {!capturedImage && !cameraActive && (
          <div className="bg-gray-800 rounded-xl p-12 text-center mb-6">
            <div className="text-6xl mb-4">📸</div>
            <p className="text-gray-300 mb-4">Start your camera to take a photo</p>
            <button
              onClick={startCamera}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-700 transition btn-glow"
            >
              Start Camera
            </button>
          </div>
        )}

        {cameraActive && (
          <div className="mb-6">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full rounded-xl border-2 border-orange-500/50"
            />
            <button
              onClick={capturePhoto}
              className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-700 transition btn-glow"
            >
              📸 Capture Photo
            </button>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />

        {/* Captured Image */}
        {capturedImage && (
          <div className="mb-6">
            <img src={capturedImage} alt="Captured" className="w-full rounded-xl border-2 border-orange-500/50 mb-4" />
            <button
              onClick={retakePhoto}
              className="w-full mb-4 px-6 py-3 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition"
            >
              🔄 Retake Photo
            </button>

            {/* Generate Caption */}
            <button
              onClick={generateCaption}
              disabled={loading}
              className="w-full mb-4 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-700 transition disabled:opacity-50 btn-glow"
            >
              {loading ? 'Generating...' : '✨ Generate AI Caption'}
            </button>

            {/* Caption */}
            {generatedCaption && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Generated Caption</label>
                <textarea
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none mb-3"
                  rows="4"
                  value={generatedCaption}
                  onChange={(e) => setGeneratedCaption(e.target.value)}
                />
                <button
                  onClick={savePost}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition btn-glow"
                >
                  {loading ? 'Saving...' : '💾 Save Post'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error Messages */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default CameraModal;
